import { logRecordingAnalysisOutcome, type RecordingAnalysisOutcome } from './telemetry';
import { recorderUploadBridge, type RecorderUploadBridge } from './upload-bridge';
import {
  coerceWorkflowWirePayload,
  RecorderUploaderError,
  type RecorderUploadAuthContext,
  type RecorderUploaderErrorCode,
  type RecorderUploaderResult,
  type RecorderUploadPreparation,
} from './upload-types';

type RecorderUploaderResponseHeaders =
  | { readonly get: (name: string) => string | null }
  | Readonly<Record<string, string | string[] | undefined>>;

interface RecorderUploaderHttp {
  get(
    url: string,
    config?: { readonly timeout?: number }
  ): Promise<{
    readonly data: unknown;
    readonly headers?: RecorderUploaderResponseHeaders;
  }>;
}

export interface CreateRecorderUploaderClientOptions {
  readonly bridge?: RecorderUploadBridge;
  readonly http: RecorderUploaderHttp;
  readonly pollDelaysMs?: readonly number[];
  readonly buildAuthContext: () => RecorderUploadAuthContext;
  readonly random?: () => number;
}

const DEFAULT_POLL_DELAYS_MS = [5_000, 10_000, 30_000, 120_000] as const;
const MAX_RETRY_AFTER_MS = 120_000;
const PREPARE_ARCHIVE_TIMEOUT_MS = 5 * 60_000;
const POLL_ANALYSIS_TIMEOUT_MS = 15 * 60_000;
const POLL_REQUEST_TIMEOUT_MS = 30_000;
const JITTER_FACTOR = 0.2;

class RecorderUploaderClient {
  private readonly activeUploads = new Map<
    string,
    {
      readonly kind: 'upload' | 'retry';
      readonly promise: Promise<RecorderUploaderResult>;
    }
  >();
  private readonly activePolls = new Map<string, Promise<RecorderUploaderResult>>();
  private readonly cancelledPolls = new Set<string>();

  constructor(
    private readonly bridge: RecorderUploadBridge,
    private readonly http: RecorderUploaderHttp,
    private readonly pollDelaysMs: readonly number[],
    private readonly buildAuthContext: () => RecorderUploadAuthContext,
    private readonly random: () => number
  ) {}

  uploadNamed(sessionId: string, name: string): Promise<RecorderUploaderResult> {
    const active = this.activeUploads.get(sessionId);
    if (active) return active.promise;
    return this.trackUpload(sessionId, 'upload', this.uploadNamedOnce(sessionId, name));
  }

  resumePolling(sessionId: string, recordingId: string): Promise<RecorderUploaderResult> {
    const inflightUpload = this.activeUploads.get(sessionId);
    if (inflightUpload) return inflightUpload.promise;
    const active = this.activePolls.get(sessionId);
    if (active) return active;

    const poll = this.pollAnalysis(sessionId, recordingId, {
      ok: false,
      error: new RecorderUploaderError('UNKNOWN'),
    }).finally(() => {
      this.activePolls.delete(sessionId);
      this.cancelledPolls.delete(sessionId);
    });
    this.activePolls.set(sessionId, poll);
    return poll;
  }

  /**
   * Stops watching a session's analysis, for a recording the user deleted. The
   * poll ends before its next request and never writes into the deleted session.
   */
  cancelPolling(sessionId: string): void {
    if (this.activeUploads.has(sessionId) || this.activePolls.has(sessionId)) {
      this.cancelledPolls.add(sessionId);
    }
  }

  retryUpload(sessionId: string): Promise<RecorderUploaderResult> {
    const active = this.activeUploads.get(sessionId);
    if (active?.kind === 'retry') return active.promise;
    const retry = active
      ? active.promise.then(
          () => this.retryUploadOnce(sessionId),
          () => this.retryUploadOnce(sessionId)
        )
      : this.retryUploadOnce(sessionId);
    return this.trackUpload(sessionId, 'retry', retry);
  }

  private trackUpload(
    sessionId: string,
    kind: 'upload' | 'retry',
    operation: Promise<RecorderUploaderResult>
  ): Promise<RecorderUploaderResult> {
    const tracked = operation.finally(() => {
      if (this.activeUploads.get(sessionId)?.promise === tracked) {
        this.activeUploads.delete(sessionId);
        this.cancelledPolls.delete(sessionId);
      }
    });
    this.activeUploads.set(sessionId, { kind, promise: tracked });
    return tracked;
  }

  private async retryUploadOnce(sessionId: string): Promise<RecorderUploaderResult> {
    const prepared = await this.bridge.retryUpload(sessionId);
    if (!prepared.ok) return prepared;
    const name = prepared.preparation.state.recordingName;
    if (!name || !canUpload(prepared.preparation)) {
      return { ok: true, state: prepared.preparation.state };
    }
    return this.performAndPoll(sessionId, name);
  }

  private async uploadNamedOnce(
    sessionId: string,
    name: string
  ): Promise<RecorderUploaderResult> {
    const prepared = await this.bridge.prepareUpload(sessionId, name);
    if (!prepared.ok) return prepared;
    if (!canUpload(prepared.preparation)) {
      if (prepared.preparation.state.stage === 'pending') {
        return this.waitForPreparedArchive(sessionId, name);
      }
      return { ok: true, state: prepared.preparation.state };
    }

    return this.performAndPoll(sessionId, name);
  }

  private async waitForPreparedArchive(
    sessionId: string,
    name: string
  ): Promise<RecorderUploaderResult> {
    await this.bridge.init();

    return new Promise((resolve) => {
      let settled = false;

      const finish = (result: RecorderUploaderResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(result);
      };

      const tryUpload = async () => {
        const prepared = await this.bridge.prepareUpload(sessionId, name);
        if (!prepared.ok) {
          finish(prepared);
          return;
        }
        if (canUpload(prepared.preparation)) {
          finish(await this.performAndPoll(sessionId, name));
        }
      };

      const unsubscribe = this.bridge.subscribeUploadState((payload) => {
        if (payload.sessionId !== sessionId) return;
        if (payload.stage === 'failed') {
          finish({
            ok: false,
            error: new RecorderUploaderError(
              payload.errorCode ?? 'UPLOAD_ARCHIVE_FAILED'
            ),
          });
          return;
        }
        if (payload.stage !== 'pre-zipped') return;
        void tryUpload();
      });

      const timeoutId = setTimeout(() => {
        finish({
          ok: false,
          error: new RecorderUploaderError('UPLOAD_ARCHIVE_FAILED'),
        });
      }, PREPARE_ARCHIVE_TIMEOUT_MS);

      void tryUpload();
    });
  }

  private async performAndPoll(
    sessionId: string,
    name: string
  ): Promise<RecorderUploaderResult> {
    try {
      const outcome = await this.bridge.performUpload(
        sessionId,
        name,
        this.buildAuthContext()
      );
      return this.pollAnalysis(sessionId, outcome.recordingId, {
        ok: true,
        state: outcome.state,
      });
    } catch (error) {
      const code: RecorderUploaderErrorCode =
        error instanceof RecorderUploaderError ? error.code : 'UNKNOWN';
      try {
        const state = await this.bridge.getUploadState(sessionId);
        if (state.stage === 'failed') {
          return { ok: true, state };
        }
      } catch {
        // fall through to explicit failure result
      }
      return { ok: false, error: new RecorderUploaderError(code, { cause: error }) };
    }
  }

  private async pollAnalysis(
    sessionId: string,
    recordingId: string,
    fallback: RecorderUploaderResult
  ): Promise<RecorderUploaderResult> {
    const startedAt = Date.now();

    const logOutcome = (
      outcome: RecordingAnalysisOutcome,
      polls: number,
      errorCode?: string
    ): void =>
      logRecordingAnalysisOutcome({
        outcome,
        sessionId,
        recordingId,
        durationMs: Date.now() - startedAt,
        polls,
        errorCode,
      });

    const cancelled = (): boolean => this.cancelledPolls.delete(sessionId);
    const cancelledResult: RecorderUploaderResult = {
      ok: false,
      error: new RecorderUploaderError('UNKNOWN'),
    };

    for (let attempt = 0; ; attempt += 1) {
      if (cancelled()) return cancelledResult;
      if (Date.now() - startedAt >= POLL_ANALYSIS_TIMEOUT_MS) {
        // The backend never reported a failure; keep that distinct from its
        // GENERATION_FAILED in telemetry. The sidecar keeps the UI's code.
        logOutcome('timeout', attempt, 'ANALYSIS_TIMEOUT');
        const state = await this.bridge.record(sessionId, {
          kind: 'analysis-failed',
          recordingId,
          errorCode: 'GENERATION_FAILED',
          errorMessage: 'Routine generation timed out',
        });
        return { ok: true, state };
      }

      let response: Awaited<ReturnType<RecorderUploaderHttp['get']>>;
      try {
        response = await this.http.get(
          `/rpa/recordings/${encodeURIComponent(recordingId)}`,
          { timeout: POLL_REQUEST_TIMEOUT_MS }
        );
      } catch {
        const scheduledMs =
          this.pollDelaysMs[Math.min(attempt, this.pollDelaysMs.length - 1)];
        // Unreachable with the default schedule; only an injected empty one lands here.
        if (scheduledMs == null) return fallback;
        const delayMs = Math.round(scheduledMs * (1 + this.random() * JITTER_FACTOR));
        await delay(delayMs);
        continue;
      }
      if (cancelled()) return cancelledResult;
      const status = statusFromResponse(response.data);
      if (status.status === 'ready') {
        logOutcome('ready', attempt + 1);
        const state = await this.bridge.record(sessionId, {
          kind: 'analysis-ready',
          recordingId,
          workflowData: coerceWorkflowWirePayload(status.workflowData),
        });
        return { ok: true, state };
      }
      if (status.status === 'failed') {
        logOutcome('failed', attempt + 1, status.errorCode);
        const state = await this.bridge.record(sessionId, {
          kind: 'analysis-failed',
          recordingId,
          errorCode: status.errorCode as RecorderUploaderErrorCode | undefined,
          errorMessage: status.errorMessage,
        });
        return { ok: true, state };
      }

      const scheduledMs =
        this.pollDelaysMs[Math.min(attempt, this.pollDelaysMs.length - 1)];
      // Unreachable with the default schedule; only an injected empty one lands here.
      if (scheduledMs == null) return fallback;

      const retryAfterMs = parseRetryAfterMs(response.headers, Date.now());
      const delayMs =
        retryAfterMs ?? Math.round(scheduledMs * (1 + this.random() * JITTER_FACTOR));
      await delay(delayMs);
    }
  }
}

function canUpload(preparation: RecorderUploadPreparation): boolean {
  return preparation.state.stage === 'pre-zipped';
}

function statusFromResponse(response: unknown): {
  readonly status: string | undefined;
  readonly workflowData: unknown;
  readonly errorCode: string | undefined;
  readonly errorMessage: string | undefined;
} {
  if (typeof response !== 'object' || response === null) {
    return {
      status: undefined,
      workflowData: null,
      errorCode: undefined,
      errorMessage: undefined,
    };
  }
  const payload = response as {
    readonly status?: unknown;
    readonly workflow_data?: unknown;
    readonly workflowData?: unknown;
    readonly error_code?: unknown;
    readonly errorCode?: unknown;
    readonly error_message?: unknown;
    readonly errorMessage?: unknown;
  };
  const errorCode = payload.error_code ?? payload.errorCode;
  const errorMessage = payload.error_message ?? payload.errorMessage;
  return {
    status: typeof payload.status === 'string' ? payload.status : undefined,
    workflowData: payload.workflow_data ?? payload.workflowData ?? null,
    errorCode: typeof errorCode === 'string' ? errorCode : undefined,
    errorMessage: typeof errorMessage === 'string' ? errorMessage : undefined,
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readHeader(
  headers: RecorderUploaderResponseHeaders | undefined,
  name: string
): string | undefined {
  if (!headers) return undefined;
  if (typeof (headers as { get?: unknown }).get === 'function') {
    const value = (headers as { get: (n: string) => string | null }).get(name);
    return value ?? undefined;
  }
  const record = headers as Record<string, string | string[] | undefined>;
  const lower = name.toLowerCase();
  for (const key of Object.keys(record)) {
    if (key.toLowerCase() === lower) {
      const value = record[key];
      return Array.isArray(value) ? value[0] : value;
    }
  }
  return undefined;
}

function parseRetryAfterMs(
  headers: RecorderUploaderResponseHeaders | undefined,
  nowMs: number
): number | undefined {
  const raw = readHeader(headers, 'Retry-After')?.trim();
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0 && /^\d+(\.\d+)?$/.test(raw)) {
    return Math.min(Math.round(seconds * 1000), MAX_RETRY_AFTER_MS);
  }
  const parsed = Date.parse(raw);
  if (Number.isFinite(parsed)) {
    const diff = parsed - nowMs;
    if (diff <= 0) return 0;
    return Math.min(diff, MAX_RETRY_AFTER_MS);
  }
  return undefined;
}

export function createRecorderUploaderClient({
  bridge = recorderUploadBridge,
  http,
  pollDelaysMs = DEFAULT_POLL_DELAYS_MS,
  buildAuthContext,
  random = Math.random,
}: CreateRecorderUploaderClientOptions): RecorderUploaderClient {
  return new RecorderUploaderClient(bridge, http, pollDelaysMs, buildAuthContext, random);
}
